import React, { useEffect, useState, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { adminService } from '../../../../services';
import type { AdminCourse, AdminCoursePayload } from '../../../../types/admin';
import CourseFormModal from './CourseFormModal';
import './AdminCourses.css';

const formatPrice = (price: number | null): string =>
  price === null ? 'Non acquistabile' : `€ ${price.toFixed(2)}`;

const TableSkeleton: React.FC = () => (
  <div className="acr-skeleton-rows">
    {Array.from({ length: 3 }).map((_, i) => (
      <div key={i} className="acr-skeleton-row">
        <div className="acr-skeleton acr-skeleton--thumb" />
        <div className="acr-skeleton-cell">
          <div className="acr-skeleton acr-skeleton--title" />
          <div className="acr-skeleton acr-skeleton--subtitle" />
        </div>
        <div className="acr-skeleton acr-skeleton--badge" />
        <div className="acr-skeleton acr-skeleton--price" />
      </div>
    ))}
  </div>
);

const AdminCourses: React.FC = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const basePath = pathname.startsWith('/superadmin')
    ? '/superadmin'
    : '/admin';

  const [courses, setCourses] = useState<AdminCourse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [editingCourse, setEditingCourse] = useState<AdminCourse | null>(null);
  const [creating, setCreating] = useState(false);

  const loadCourses = () => {
    setLoading(true);
    adminService
      .getCourses()
      .then(setCourses)
      .catch(() => setError('Impossibile caricare i corsi.'))
      .finally(() => setLoading(false));
  };

  useEffect(loadCourses, []);

  const filtered = useMemo(() => {
    if (!search.trim()) return courses;
    const q = search.toLowerCase();
    return courses.filter((c) => c.title.toLowerCase().includes(q));
  }, [courses, search]);

  const handleSave = async (payload: AdminCoursePayload) => {
    if (editingCourse) {
      const updated = await adminService.updateCourse(
        editingCourse.id,
        payload
      );
      setCourses((prev) =>
        prev.map((c) => (c.id === updated.id ? updated : c))
      );
    } else {
      const created = await adminService.createCourse(payload);
      setCourses((prev) => [...prev, created]);
    }
    setEditingCourse(null);
    setCreating(false);
  };

  const handleViewEnrollments = (course: AdminCourse) => {
    navigate(`${basePath}/utenti?corso=${encodeURIComponent(course.id)}`, {
      state: { courseTitle: course.title },
    });
  };

  return (
    <div className="acr-page">
      <div className="acr-page-header">
        <div>
          <h1 className="acr-page-title">Corsi</h1>
          <p className="acr-page-sub">
            {loading ? '…' : `${filtered.length} di ${courses.length} corsi`}
          </p>
        </div>
        <button
          className="acr-action-btn acr-action-btn--primary"
          onClick={() => setCreating(true)}
        >
          <i className="bi bi-plus-lg me-2" />
          Nuovo corso
        </button>
      </div>

      <div className="acr-toolbar">
        <div className="acr-search-wrap">
          <i className="bi bi-search acr-search-icon" aria-hidden="true" />
          <input
            type="search"
            className="acr-search"
            placeholder="Cerca per titolo…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Cerca corsi"
          />
        </div>
      </div>

      {error && (
        <div className="acr-error" role="alert">
          <i className="bi bi-exclamation-triangle me-2" />
          {error}
        </div>
      )}

      {loading ? (
        <TableSkeleton />
      ) : filtered.length === 0 ? (
        <div className="acr-empty">
          <i className="bi bi-collection-play acr-empty-icon" />
          <p className="acr-empty-title">Nessun corso trovato</p>
        </div>
      ) : (
        <div className="acr-list">
          {filtered.map((course) => (
            <div key={course.id} className="acr-card">
              <div className="acr-card__thumb">
                {course.imageUrl ? (
                  <img src={course.imageUrl} alt={course.title} />
                ) : (
                  <i className="bi bi-collection-play" />
                )}
              </div>
              <div className="acr-card__info">
                <p className="acr-card__title">{course.title}</p>
                {course.subtitle && (
                  <p className="acr-card__subtitle">{course.subtitle}</p>
                )}
                <div className="acr-card__meta">
                  <span
                    className={`acr-badge ${course.price !== null ? 'acr-badge--published' : 'acr-badge--draft'}`}
                  >
                    {course.price !== null ? 'In vendita' : 'Coming soon'}
                  </span>
                  <span className="acr-card__price">
                    {formatPrice(course.price)}
                  </span>
                  <button
                    type="button"
                    className="acr-card__enrollments acr-card__enrollments--link"
                    onClick={() => handleViewEnrollments(course)}
                    title="Vedi utenti iscritti a questo corso"
                  >
                    <i className="bi bi-people me-1" />
                    {course.enrollments} iscritti
                  </button>
                </div>
              </div>
              <div className="acr-card__actions">
                <button
                  className="acr-icon-btn"
                  onClick={() => setEditingCourse(course)}
                  title="Modifica corso"
                  aria-label="Modifica corso"
                >
                  <i className="bi bi-pencil" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {(creating || editingCourse) && (
        <CourseFormModal
          course={editingCourse}
          onClose={() => {
            setCreating(false);
            setEditingCourse(null);
          }}
          onSave={handleSave}
        />
      )}
    </div>
  );
};

export default AdminCourses;
